import { useCallback, useEffect, useMemo, useState } from "react";
import UserAvatar from "../../components/userAvatar/UserAvatar";
import EventList from "../../components/eventList/EventList";
import { getPublicOrganizedEvents } from "../profile/services/organizedEventsShare.api";
import styles from "./PublicOrganizerEventsPage.module.css";

// "loading" | "ready" | "expired" | "network-error"
function usePublicOrganizedEvents(token) {
  const [state, setState] = useState("loading");
  const [organizer, setOrganizer] = useState(null);
  const [events, setEvents] = useState([]);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState("loading");

    getPublicOrganizedEvents(token)
      .then((payload) => {
        if (cancelled) return;
        setOrganizer(payload?.organizer || null);
        setEvents(Array.isArray(payload?.events) ? payload.events : []);
        setState("ready");
      })
      .catch((error) => {
        if (cancelled) return;
        if (error?.status === 404) {
          setState("expired");
        } else {
          setState("network-error");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token, reloadToken]);

  const retry = useCallback(() => {
    setReloadToken((value) => value + 1);
  }, []);

  return { state, organizer, events, retry };
}

// Restaure les meta robots/referrer precedents au demontage : cette page ne
// doit jamais laisser une regle "noindex"/"no-referrer" fuiter sur le reste
// de l'app si l'utilisateur navigue ensuite en SPA depuis ici (en pratique
// l'app recharge sur ce chemin, mais on reste correct dans tous les cas).
function usePublicShareMetaTags() {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const robotsMeta = document.querySelector('meta[name="robots"]');
    const referrerMeta = document.querySelector('meta[name="referrer"]');
    const previousRobotsContent = robotsMeta?.getAttribute("content") ?? null;
    const previousReferrerContent = referrerMeta?.getAttribute("content") ?? null;
    const createdRobotsMeta = !robotsMeta;
    const createdReferrerMeta = !referrerMeta;

    const activeRobotsMeta = robotsMeta || document.createElement("meta");
    activeRobotsMeta.setAttribute("name", "robots");
    activeRobotsMeta.setAttribute("content", "noindex,nofollow");
    if (createdRobotsMeta) document.head.appendChild(activeRobotsMeta);

    const activeReferrerMeta = referrerMeta || document.createElement("meta");
    activeReferrerMeta.setAttribute("name", "referrer");
    activeReferrerMeta.setAttribute("content", "no-referrer");
    if (createdReferrerMeta) document.head.appendChild(activeReferrerMeta);

    return () => {
      if (createdRobotsMeta) {
        activeRobotsMeta.remove();
      } else if (previousRobotsContent !== null) {
        activeRobotsMeta.setAttribute("content", previousRobotsContent);
      }

      if (createdReferrerMeta) {
        activeReferrerMeta.remove();
      } else if (previousReferrerContent !== null) {
        activeReferrerMeta.setAttribute("content", previousReferrerContent);
      }
    };
  }, []);
}

function LoadingState() {
  return (
    <div className={styles.stateBlock} role="status" aria-live="polite">
      <span className={styles.spinner} aria-hidden="true" />
      <p>Chargement des sorties…</p>
    </div>
  );
}

function ExpiredState() {
  return (
    <div className={styles.stateBlock}>
      <h1 className={styles.stateTitle}>Lien introuvable ou expiré</h1>
      <p className={styles.stateBody}>
        Ce lien n'est plus valide. Demande à la personne concernée de t'en partager un nouveau.
      </p>
      <DiscoverKarangoButton />
    </div>
  );
}

function NetworkErrorState({ onRetry }) {
  return (
    <div className={styles.stateBlock}>
      <h1 className={styles.stateTitle}>Un problème est survenu</h1>
      <p className={styles.stateBody}>Impossible de charger cette page pour le moment.</p>
      <button type="button" className={styles.primaryButton} onClick={onRetry}>
        Réessayer
      </button>
    </div>
  );
}

function EmptyEventsState() {
  return (
    <div className={styles.stateBlock}>
      <h1 className={styles.stateTitle}>Aucune sortie à venir</h1>
      <p className={styles.stateBody}>
        Cette personne n'a pas de sortie publique prévue pour le moment. Reviens un peu plus tard !
      </p>
    </div>
  );
}

function DiscoverKarangoButton() {
  return (
    <a className={styles.secondaryButton} href="/">
      Découvrir Karango
    </a>
  );
}

export default function PublicOrganizerEventsPage({ token }) {
  usePublicShareMetaTags();

  useEffect(() => {
    document.documentElement.classList.add("karango-public-share-page");
    document.body.classList.add("karango-public-share-page");

    return () => {
      document.documentElement.classList.remove("karango-public-share-page");
      document.body.classList.remove("karango-public-share-page");
    };
  }, []);

  const { state, organizer, events, retry } = usePublicOrganizedEvents(token);
  // Cette page est un point d'entree standalone (App.js la rend a la place
  // de RootLayout), donc pas d'overlay de detail disponible ici. Un clic
  // renvoie vers l'appli normale sur /events/:id, que parseDeepLinkFromLocation
  // (AppContext.js) sait deja ouvrir au demarrage SANS authentification —
  // deepLinkRequiresAuth ne l'exige que pour un sous-chemin (discussion,
  // carpool...), pas pour le detail de base.
  const handleSelectEvent = useCallback((event) => {
    const eventId = event?.id;
    if (!eventId || typeof window === "undefined") return;
    window.location.assign(`/events/${encodeURIComponent(eventId)}`);
  }, []);

  // EventList est virtualise (@tanstack/react-virtual) et a besoin d'un
  // conteneur scrollable mesurable pour calculer quoi rendre : le
  // scrollContainerSelector par defaut ("[data-panel-scroll]") n'existe nulle
  // part sur cette page standalone, ce qui faisait rendre 0 ligne meme quand
  // l'API renvoyait bien des evenements. On fournit donc explicitement notre
  // propre element scrollable via scrollElementRef.
  // Une ref objet (`useRef`) change sa propriete `.current` sans provoquer de
  // nouveau rendu. Lors du tout premier affichage de cette page standalone,
  // EventList pouvait donc initialiser son virtualiseur avec `null` et rester
  // noir jusqu'a un rendu sans rapport (par exemple ouvrir puis fermer la
  // connexion). Une callback-ref stockee en state rend l'element reel
  // disponible au virtualiseur de facon deterministe des son montage.
  const [listShellElement, setListShellElement] = useState(null);
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => setIsMobileViewport(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const organizerName = useMemo(
    () => organizer?.displayName || organizer?.username || "cet organisateur",
    [organizer]
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        {organizer ? (
          <>
            <UserAvatar
              user={organizer}
              name={organizer.displayName}
              size={64}
              className={styles.headerAvatar}
            />
            <h1 className={styles.headerTitle}>Les prochaines sorties de {organizerName}</h1>
          </>
        ) : (
          <h1 className={styles.headerTitle}>Les prochaines sorties</h1>
        )}
      </header>

      <main className={styles.main}>
        {state === "loading" ? <LoadingState /> : null}
        {state === "expired" ? <ExpiredState /> : null}
        {state === "network-error" ? <NetworkErrorState onRetry={retry} /> : null}
        {state === "ready" && events.length === 0 ? <EmptyEventsState /> : null}
        {state === "ready" && events.length > 0 ? (
          <div className={styles.listShell} ref={setListShellElement}>
            <EventList
              events={events}
              readOnly
              mobile={isMobileViewport}
              scrollElementRef={listShellElement}
              onSelectEvent={handleSelectEvent}
              emptyTitle="Aucune sortie à venir"
            />
          </div>
        ) : null}
      </main>

      {state === "ready" ? (
        <footer className={styles.footer}>
          <DiscoverKarangoButton />
        </footer>
      ) : null}
    </div>
  );
}
